import { Button, ButtonProps } from '@mui/material';
import { LOGIN_PHRASES } from '@/consts/phrases/loginPhrases';
import { useEffect, useState } from 'react';
import { usePhrase, useInterval, useCompletionStatus } from '@/hooks';
import { CompletionStatus } from '@/types/enums/completionStatus';
import { ACCOUNT_CONTACT_DETAILS_PHRASES } from '@/consts/phrases/accountPhrases';
import { WithCompletionStatus } from '../WithCompletionStatus/WithCompletionStatus';
import { isNumber } from '@/types';

interface SendCodeButtonProps extends Omit<ButtonProps, 'onClick'> {
    onClick: () => Promise<boolean>;
    refreshInterval?: number;
    buttonProps?: ButtonProps;
    codeType?: 'email' | 'phone';
}

export const SendCodeButton = (props: SendCodeButtonProps) => {
    const { onClick, refreshInterval, children, buttonProps, codeType: _, ...other } = props;
    const [timer, setTimer] = useState<number | undefined>(refreshInterval);
    const timerHasEnded = timer === 0;
    const timerIsRunning = !!(timer && timer > 0);

    const { t } = usePhrase();
    const { completionStatus, getCompletionStatus, setCompletionStatus, resetCompletionStatus } =
        useCompletionStatus();

    useEffect(() => {
        setTimer(refreshInterval);
    }, [refreshInterval]);

    useInterval(() => {
        if (timerHasEnded) return;
        if (isNumber(timer)) {
            setTimer(timer - 1);
        }
    }, 1000);

    const [isSending, setIsSending] = useState<boolean>(false);

    const sendCode = async () => {
        try {
            setIsSending(true);
            const codeSent = await getCompletionStatus(() => onClick());

            if (codeSent) {
                setCompletionStatus(CompletionStatus.SUCCEEDED);
            } else {
                setCompletionStatus(CompletionStatus.FAILED);
            }
        } finally {
            setIsSending(false);
        }
    };

    return (
        <WithCompletionStatus
            status={completionStatus}
            resetStatus={resetCompletionStatus}
            successMessage={ACCOUNT_CONTACT_DETAILS_PHRASES.OTC_SENT}
        >
            <Button
                {...other}
                variant={buttonProps?.variant ?? 'outlined'}
                disabled={isSending || timerIsRunning}
                fullWidth={buttonProps?.fullWidth ?? true}
                onClick={sendCode}
                data-testid="send-code-button"
            >
                {isNumber(timer) && timerIsRunning
                    ? `${t(LOGIN_PHRASES.NEXT_CODE_IN, { asString: true })} ${
                          timer < 10 ? `0${timer}` : timer
                      } ${t(LOGIN_PHRASES.SECS, { asString: true })}`
                    : children || t(LOGIN_PHRASES.SEND_CODE)}
            </Button>
        </WithCompletionStatus>
    );
};
